import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Users, Plus, Minus, Baby, User, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Child {
  age: number;
}

export interface GuestData {
  adults: number;
  children: Child[];
}

interface GuestSelectorProps {
  value: GuestData;
  onChange: (value: GuestData) => void;
  maxGuests?: number;
  compact?: boolean;
  inline?: boolean;
  className?: string;
}

export const GuestSelector = ({ 
  value, 
  onChange, 
  maxGuests = 20,
  compact = false,
  inline = false,
  className 
}: GuestSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const totalGuests = value.adults + value.children.length;

  const incrementAdults = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (totalGuests < maxGuests) {
      onChange({ ...value, adults: value.adults + 1 });
    }
  };

  const decrementAdults = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (value.adults > 1) {
      onChange({ ...value, adults: value.adults - 1 });
    }
  };

  const incrementChildren = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (totalGuests < maxGuests) {
      onChange({ ...value, children: [...value.children, { age: 5 }] });
    }
  };

  const decrementChildren = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (value.children.length > 0) {
      const newChildren = value.children.slice(0, -1);
      onChange({ ...value, children: newChildren });
    }
  };

  const addChild = () => {
    if (totalGuests < maxGuests) {
      onChange({ ...value, children: [...value.children, { age: 5 }] });
    }
  };

  const removeChild = (index: number) => {
    const newChildren = value.children.filter((_, i) => i !== index);
    onChange({ ...value, children: newChildren });
  };

  const updateChildAge = (index: number, age: number) => {
    const newChildren = [...value.children];
    newChildren[index] = { age };
    onChange({ ...value, children: newChildren });
  };

  // Option C: Compact trigger with visible +/- for adults, popover for children
  if (compact || inline) {
    return (
      <div className={cn("flex items-center gap-4 h-12 bg-muted/30 rounded-md px-3", className)}>
        {/* Adults with inline +/- */}
        <div className="flex items-center gap-1.5">
          <User className="h-4 w-4 text-muted-foreground" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full hover:bg-muted"
            onClick={decrementAdults}
            disabled={value.adults <= 1}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-4 text-center text-sm font-medium">{value.adults}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full hover:bg-muted"
            onClick={incrementAdults}
            disabled={totalGuests >= maxGuests}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>

        {/* Children - Button that opens popover */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-8 px-3 text-sm font-medium hover:bg-muted gap-1.5 whitespace-nowrap shrink-0"
            >
              <Baby className="h-4 w-4 text-muted-foreground" />
              {value.children.length === 0 
                ? "Kinder" 
                : value.children.length === 1 
                  ? "1 Kind" 
                  : `${value.children.length} Kinder`}
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Kinder</p>
                  <p className="text-xs text-muted-foreground">0-12 Jahre</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => decrementChildren()}
                    disabled={value.children.length === 0}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-6 text-center font-medium">{value.children.length}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => incrementChildren()}
                    disabled={totalGuests >= maxGuests}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Child ages */}
              {value.children.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">Alter der Kinder</p>
                  {value.children.map((child, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground w-14">
                        Kind {index + 1}:
                      </Label>
                      <select
                        value={child.age}
                        onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                        className="flex-1 h-8 px-2 text-sm border rounded-md bg-background"
                      >
                        {Array.from({ length: 18 }, (_, i) => (
                          <option key={i} value={i}>
                            {i === 0 ? "Unter 1" : `${i} ${i === 1 ? "Jahr" : "Jahre"}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Full expanded view (for booking forms etc.)
  return (
    <div className={cn("space-y-4", className)}>
      <GuestSelectorContent
        value={value}
        maxGuests={maxGuests}
        totalGuests={totalGuests}
        onIncrementAdults={() => incrementAdults()}
        onDecrementAdults={() => decrementAdults()}
        onAddChild={addChild}
        onRemoveChild={removeChild}
        onUpdateChildAge={updateChildAge}
      />
    </div>
  );
};

interface GuestSelectorContentProps {
  value: GuestData;
  maxGuests: number;
  totalGuests: number;
  onIncrementAdults: () => void;
  onDecrementAdults: () => void;
  onAddChild: () => void;
  onRemoveChild: (index: number) => void;
  onUpdateChildAge: (index: number, age: number) => void;
}

const GuestSelectorContent = ({
  value,
  maxGuests,
  totalGuests,
  onIncrementAdults,
  onDecrementAdults,
  onAddChild,
  onRemoveChild,
  onUpdateChildAge
}: GuestSelectorContentProps) => {
  return (
    <div className="space-y-4">
      {/* Adults */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Erwachsene</p>
            <p className="text-xs text-muted-foreground">Ab 13 Jahre</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={onDecrementAdults}
            disabled={value.adults <= 1}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-8 text-center font-medium">{value.adults}</span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={onIncrementAdults}
            disabled={totalGuests >= maxGuests}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Children */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Baby className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Kinder</p>
              <p className="text-xs text-muted-foreground">0-12 Jahre</p>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddChild}
            disabled={totalGuests >= maxGuests}
            className="h-8"
          >
            <Plus className="h-3 w-3 mr-1" />
            Kind hinzufügen
          </Button>
        </div>

        {/* Child age inputs */}
        {value.children.length > 0 && (
          <div className="space-y-2 pl-6 border-l-2 border-muted">
            {value.children.map((child, index) => (
              <div key={index} className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground w-16">
                  Kind {index + 1}:
                </Label>
                <select
                  value={child.age}
                  onChange={(e) => onUpdateChildAge(index, parseInt(e.target.value))}
                  className="flex-1 h-8 px-2 text-sm border rounded-md bg-background"
                >
                  {Array.from({ length: 18 }, (_, i) => (
                    <option key={i} value={i}>
                      {i === 0 ? "Unter 1 Jahr" : `${i} ${i === 1 ? "Jahr" : "Jahre"}`}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => onRemoveChild(index)}
                >
                  <Minus className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="pt-3 border-t text-xs text-muted-foreground">
        Gesamt: {totalGuests} von {maxGuests} Gästen
      </div>
    </div>
  );
};

export default GuestSelector;
